/*
 * 版权所有 (c) 华为技术有限公司 2024
 * Copyright (c) Huawei Technologies Co., Ltd. 2024. All rights reserved.
 */
import { Injectable } from '@angular/core';
import { I18nService } from '@dm/core';
import { type SelectOption } from '@dm/core/types';
import { type CIFSClient } from 'app/common/types/mo';
import { ProductConfig } from 'app/config';

import { GuideItemE } from '../../file-system/file-system-guide/file-system-guide.service';
import { type SmartguiCifsshareReceived } from '../share';
import { ShareTypeE } from '../share.enum';
import { AuthNameE, DomainTypeE, UserTypeE } from './client-cifs-share/client-clifs-share';

export enum OffLineFileModeE {
    None = '1',
    Manual = '2',
    Documents = '3',
    Programs = '4'
}

@Injectable()
export class CifsShareStrService {
    // cifs共享名称长度
    cifsShareNameMinLen = 1;
    cifsShareNameMaxLen = 80;

    i18n = {
        btn: {
            createAsBtn: this.get('common.createSchema_btn'),
            createBtn: this.get('common.create_button'),
            deleteBtn: this.get('common.delete_button'),
            modifyBtn: this.get('common.modify_button'),
            okBtn: this.get('common.confirm_button'),
            cancelBtn: this.get('common.cancel_button'),
            closeBtn: this.get('common.close_button'),
            actionBtn: this.get('common.operation_button'),
            addBtn: this.get('common.add_button'),
            removeBtn: this.get('common.remove_button'),
            addClient: this.get('service.share_term_createCIFS_addUserorGroup_buttton'),
            modifyClient: this.get('service.share_term_createCIFS_addUserorGroup_buttton'),
            deleteClient: this.get('service.share_term_createCIFS_addUserorGroup_buttton'),
            createHomedirBtn: this.get('service.share_term_createHomedirShare_btn'),
            addHomedirBtn: this.get('service.share_term_createCIFSHomedir_addMapRule_title'),
            addExtensionFilteringBtn: this.get('service.share_task_filenameextensionfiltering_add_title'),
            addIpWhiteListBtn: this.get('service.share_task_accessibleIP_AddIPAddress_title'),
        },
        label: {
            //  名称
            name: this.get('common.name_column'),
            // 用户名
            userName: this.get('common.username_label'),
            // 优先级
            priority: this.get('common.priority_lab'),
            //  多个客户端
            clients: this.get('service.share_term_addClient_clients_lab'),
            //  单个客户端
            client: this.get('service.share_term_addClient_client_lab'),
            // 描述
            description: this.get('common.description_column'),
            // 文件系统
            fileSystem: this.get('service.quota_term_create_FS_lab'),
            // 所属租户
            vStoreLab: this.get('common.owningVstore_lab'),
            // dtree
            dtree: this.get('service.quota_term_create_Dtree_lab'),
            // 共享名称
            share_name: this.get('service.share_term_shareName_lab'),
            // 路径 - 表格用的
            path: this.get('service.dtree_task_create_fullPath_lab'),
            // 共享路径
            share_path: this.get('service.share_term_sharePath_lab'),
            // 相对目录
            relative_path: this.get('service.share_term_createCIFSHomedir_relativeDirectory_lab'),
            // 所属存储池
            storagePool: this.get('storage.LunGroup_term_belongedStoragePool_label'),
            // ID
            id: this.get('common.ID_column'),
            // RID
            rid: this.get('service.accessUser_task_setSecuPolicy_RID_lab'),
            // 类型
            type: this.get('common.type_column'),
            // 权限级别
            permissionLevel: this.get('service.share_term_addClient_permissionLevel_lab'),
            // 操作
            option: this.i18nService.get('common.operation_button'),
            // 高级
            advance: this.get('common.advance_label'),
            // 写入模式
            sync: this.get('service.share_term_addClient_writeMode_lab'),
            // 权限限制
            all_squash: this.get('service.share_term_addClient_permissionLimit_lab'),
            // root权限限制
            root_squash: this.get('service.share_term_addClient_rootPermissionLimit_lab'),
            // 源端口校验限制
            secure: this.get('service.share_term_addClient_sourcePortVerification_lab'),
            // windows用户
            WindowsUserLab: this.get('service.accessUser_WindowsUsers_lab'),
            // 显示以前的版本
            previousVersion: this.get('service.share_term_createCIFSHomedir_previousVersion_lab'),
            // 显示快照
            showSnapshot: this.get('service.share_term_createCIFS_showSnapshot_lab'),
            // notify
            notify: this.get('service.share_term_createCIFS_notify_lab'),
            // CA
            enableCa: this.get('service.share_term_createCIFSHomedir_CA_lab'),
            // SMB3加密
            smb3EncryptionEnable: this.get('service.share_term_createCIFS_SMB3Encrypt_lab'),
            // 非加密客户端访问
            unencryptedAccess: this.get('service.share_term_createCIFS_UnencryptedClientAccess_lab'),
            // 用户/用户组
            userOrGroup: this.get('service.quota_term_userorGroup_lab'),
            // 本地认证用户
            localUser: this.get('service.quota_term_create_userTyper_localUser_value'),
            // 本地认证用户组
            localUserGroup: this.get('service.quota_term_create_userTyper_localUserGroup_value'),
            // ad域用户
            adDomainUser: this.get('service.quota_term_create_UserType_ADdomain_value'),
            // ad域用户组
            adDomainGroup: this.get('service.quota_term_create_UserGroupType_ADdomain_value'),
            // LDAP域用户
            ldapDomainUser: this.get('service.quota_term_create_UserType_LDAPdomain_value'),
            // LDAP域用户组
            ldapDomainGroup: this.get('service.quota_term_create_UserGroupType_LDAPdomain_value'),
            // NIS域用户
            nisDomainUser: this.get('service.quota_term_create_UserType_NISdomain_value'),
            // NIS域用户组
            nisDomainGroup: this.get('service.quota_term_create_UserGroupType_NISdomain_value'),
            // 只读
            readonly: this.get('service.share_term_createCIFS_perLevel_readonly_listbox'),
            // 完全控制
            entrieControl: this.get('service.share_term_createCIFS_perLevel_entrieControl_listbox'),
            // 禁止
            forbidden_listbox: this.get('service.share_term_createCIFS_perLevel_forbidden_listbox'),
            // 读写
            rwNotAllowDelRename: this.get('service.share_term_createCIFS_perLevel_rwNotAllowDelRename_listbox'),
            // 已启用
            enabled: this.get('common.enabled_switch_column'),
            // 启用
            enable: this.get('common.enable_label'),
            // 未启用
            disable: this.get('common.disabled_switch_column'),
            // 本地认证用户/用户组
            localUserOrGroup: this.get('service.share_term_createCIFS_userType_localUserorGroup_lab'),
            // AD域用户/用户组
            adUserOrGroup: this.get('service.share_term_createCIFS_userType_ADUserorGroup_lab'),
            // 用户类型
            typeLabel: this.get('system.type_column'),
            // windows本地认证用户
            windowsLocalUser: this.get('service.share_term_createCIFS_addUser_userType_winLocalUser_listbox'),
            // windows本地认证用户组
            windowsLocalUserGroup: this.get('service.share_term_createCIFS_addUser_userType_winLocalUserGroup_listbox'),
            // Everyone
            everyone: this.get('service.share_term_createCIFS_addUser_userType_everyone_listbox'),
            // Unix本地认证用户
            unixLocalUser: this.get('service.share_term_createCIFS_addUser_userType_UNIXLocalUser_listbox'),
            // Unix本地认证组
            unixLocalUserGroup: this.get('service.share_term_createCIFS_addUser_userType_UNIXLocalUserGroup_listbox'),
            // 创建模式
            createStyle: this.get('service.share_term_createFrom_lab'),
            // copy from
            copyFrom: this.get('service.share_term_createMode_copyfrom_value'),
            // new
            new: this.get('common.newCreate_checkbox'),
            // 共享
            share: this.get('common.EnumDefine_MOTYPE_SHARE'),
            // 共享s
            shares: this.get('common.share_title'),
            // 逗号
            comma: this.get('common.comma_label'),
            leftTransferTitle: this.get('service.share_term_createCIFS_availUserorGroup_lab'),
            rightTransferTitle: this.get('service.share_term_createCIFS_selectedUserorGroup_lab'),
            // Homedir
            homedir: this.get('service.share_term_homedirShare_col'),
            // 自动创建
            autoCreate: this.get('service.share_term_createCIFSHomedir_autoCreate_checkbox'),
            auditFs: this.get('service.share_term_selectAuditFS_info'),
            ABE: this.get('service.share_term_createCIFS_ABE_lab'),
            fileNameExtensionFilter: this.get('service.share_task_filenameextensionfiltering_label'),
            // 显示Snapshot
            showSnapShotLab: this.get('service.share_term_createCIFS_showSnapshot_lab'),
            // 显示SnapshotTip
            showSnapShotTip: this.get('service.share_term_createCIFS_showSnapshot_tip'),
            // 显示以前的版本
            showPreviousVersion: this.get('service.share_term_createCIFSHomedir_previousVersion_lab'),
            // 显示以前的版本tip
            showPreviousVersionTip: this.get('service.share_term_createCIFSHomedir_previousVersion_tip'),
            // 离线文件缓存模式
            offlineCacheModeLab: this.get('service.share_task_createcifs_offlineCacheMode_label'),
        },
        value: {
            yes: this.get('common.term_yes_label'),
            no: this.get('common.term_No_label'),
        },
        title: {
            // CIFS
            cifs: this.get('common.CIFS_lab'),
            // CIFS共享
            cifsShare: this.get('common.cifsShare_lab'),
            // 模板创建cifs
            createAsTitle: this.get('service.share_term_createCIFSfromTemp_title'),
            // 创建cifs
            createTitle: this.get('service.share_term_createCIFS_title'),
            // 创建homedir
            createHomedirTitle: this.get('service.share_term_createCIFSHomedir_title'),
            // 修改homedir
            modifyHomedirTitle: this.get('service.share_term_modifyCIFSHomedir_title'),
            // 删除homedir
            deleteHomedirTitle: this.get('service.share_term_deleteCIFSHomedir_title'),
            // 基本信息
            basicInfo: this.get('common.basicInfo_title'),
            // 选择文件系统
            selectFS: this.get('service.share_term_create_chooseFilesystem_title'),
            // 选择Dtree
            selectDtree: this.get('service.share_term_create_chooseDtree_title'),
            // 删除CIFS共享
            deleteShare: this.get('service.share_term_deleteCIFS_title'),
            // 修改CIFS共享
            modifyShare: this.get('service.share_term_modifyCIFS_title'),
            // 权限列表
            permissionList: this.get('service.share_term_view_permissions_lab'),
            // 添加用户（组）
            addClient: this.get('service.share_term_createCIFS_addUserorGroup_buttton'),
            // 修改用户（组）
            modifyClient: this.get('service.share_term_createCIFS_modifyUserorGroup_title'),
            // 移除用户（组）
            removeClient: this.get('service.share_term_createCIFS_deleteUserorGroup_title'),
            // 映射规则
            mappingRule: this.get('service.share_term_createCIFSHomedir_mapRule_lab'),
            // 增加映射规则
            addMappingRule: this.get('service.share_term_createCIFSHomedir_addMapRule_title'),
            // 移除映射规则
            removeMappingRule: this.get('service.share_term_createCIFSHomedir_removeMapRule_title'),
            // 修改映射规则
            modifyMappingRule: this.get('service.share_term_createCIFSHomedir_modifyMapRule_title'),
            extensionFiltering: this.get('service.share_task_filenameextensionfilteringRule_tab'),
            /**
             * 增加CIFS共享权限
             */
            addCifsPermission: this.get('service.share_term_createCIFS_addPermission_title'),
            /** ip白名单 */
            ipWhiteList: this.get('service.share_task_accessibleIP_tab'),
        },
        tip: {
            // 请先选择所属租户。
            plsSelectVstore: this.get('service.share_term_create_chooseVstore_tip'),
            // 请先选择copyfrom的共享。
            plsSlectShare: this.get('service.share_term_create_modify_err_tip'),
            // 权限数{0}。
            permisssions: (num: number): string => this.get('service.share_term_create_permissionNum_lab', [num]),
            // 权限数{0},{1}
            permissionsWithEveryone: (num: number, str: string): string =>
                this.get('service.share_term_create_permissionNum_value_lab', [num, str]),
            // Everyone完全控制
            everyone: this.get('service.share_term_create_permission_everyoneControl_value'),
            /**
             * Everyone只读
             */
            everyoneRead: this.get('service.share_term_create_permission_everyoneRead_value'),
            // 只有加入权限列表的用户（组）才能访问
            clientsTip: this.get('service.share_term_createCIFS_addUser_desc_tip'),
            /**
             * 只有加入列表的用户或用户组才允许访问CIFS Homedir共享。
             */
            homedirClientsTip: this.get('service.share_term_createCIFSHomedir_addUser_desc_tip'),
            /**
             * 当前CIFS共享服务
             */
            CIFSServiceOffTip1: this.get('service.share_term_enableCIFS_info_lab'),
            /**
             * 开始CIFS共享服务
             */
            CIFSServiceOffTip2: this.get('service.share_term_enableCIFS_info_lab1'),
            /**
             * 共享名称不能包含如下字符\ " / \\ [ ] : | < > + ; , ? * =，同时不能使用如下系统保留名称ipc$、autohome、~和print$。
             */
            shareNameValidation: this.get('service.share_term_createCIFS_shareName_validation'),
            /**
             * homedir共享名称不能包含如下字符\ " / \\ [ ] : | < > + ; , ? * =，同时不能使用如下系统保留名称ipc$、~和print$。
             */
            homedirShareNameValidation: this.get('service.share_term_createCIFSHomedir_shareName_validation'),
            /**
             * 显示快照
             */
            showSnapshot: this.get('service.share_term_createCIFS_showSnapshot_tip'),
            /**
             * 显示以前的版本
             */
            previousVersion: this.get('service.share_term_createCIFSHomedir_previousVersion_tip'),
            /**
             * 勾选后，一个客户端针对一个目录内的变更操作（如新增目录、新增文件、修改目录、修改文件等），可被其他正在访问此目录及其父目录的客户端感知，如自动刷新显示。
             */
            notify: this.get('service.share_term_createCIFS_notify_tip'),
            /**
             * 是否启用CA提示
             */
            enableCa: this.get('service.share_term_createCIFSHomedir_CA_tip'),
            /**
             * 是否启用CA提示
             */
            smb3EncryptionEnable: this.get('service.share_term_createCIFS_SMB3Encrypt_tip'),
            /**
             * 是否启用CA提示
             */
            unencryptedAccess: this.get('service.share_term_createCIFS_UnencryptedClientAccess_tip'),
            /**
             * ad域用户多输入框提示
             */
            adMultiNameTip: this.get('service.accessUser_term_create_name_tip1', [1, 256]),
            /**
             * ad域用户组多输入框提示
             */
            adGroupMultiNameTip: this.get('service.accessUserGroup_term_create_name_tip', [1, 256]),
            /**
             * 该Dtree路径中含有“\”，在CIFS共享中“\”为目录分隔符，会导致创建的CIFS共享路径与预期不符。
             */
            dtreePathHasBackslash: this.get('service.share_term_createCIFS_selectDtree_validation'),
            /**
             * 设置优先级选项提示:数字越小，优先级越高
             */
            priority: this.get('common.priority_tip'),
            /**
             * 请输入CIFS Homedir共享相对目录。规则如下：<br/>
             * 1、最大长度支持255个字符，不能包含*:\\?”<>|中的任意一个特殊字符。<br/>
             * 2、可以包含普通字符和特殊字符串(%d或%w)。%d代表域名，%w代表用户名，且每个特殊字符串只能出现一次。<br/>
             * 输入示例1：home_china/usera/。<br/>
             * 输入示例2：%d/%w/。
             */
            relative_path: this.get('service.share_term_createCIFSHomedir_relativeDirectory_tip'),
            // CIFS Homedir 共享不支持模板创建。
            createAsHomedir: this.get('service.share_term_createMode_copyfrom_err_tip'),
            // 开启AutoCreate功能后，如果CIFS Homedir共享路径下相对目录不存在时，系统会自动创建该目录。
            autoCreate: this.get('service.share_term_createCIFSHomedir_autoCreate_tip'),
            /**
             * 用户名格式要求如下：<br/>
             * 1、可以输入本地用户或域用户的用户名。<br/>
             * 2、不能包含/\"[]<>+:;,?=|@和空格。<br/>
             * 3、对于特殊字符“\\”，不能作为首尾字符，且只能出现一次；对于特殊字符“.”，不能作为尾字符。<br/>
             * 4、域用户的名称格式为“域名\\域用户名”。<br/>
             * 5、支持通配符“*”，但只支持一个通配符，且只能作为尾字符。例如：“china\\*”表示china域下的所有用户，“abc*”表示以abc开头的所有本地用户。
             */
            userName: this.get('service.share_term_createCIFSHomedir_userName_tip'),
            // CIFS Homedir共享请在 CIFS共享页面 查看。
            showHomedir: this.get('service.share_term_createCIFSHomedir_view_info'),
            // 映射规则根据优先级从高到低排序，相同优先级按照创建先后顺序排序，用户按顺序匹配映射规则。
            mappingRule: this.get('service.share_term_createCIFSHomedir_mapRule_tip'),
            /**
             * 已选审计日志文件系统，权限列表被清空。
             */
            permissionClearTip: this.get('service.share_term_clearPermissions_msg'),
            ABE: this.get('service.share_term_createCIFS_enableABE_tip'),
            addMapRule: this.get('service.share_term_createCIFSHomedir_addMapRule_err_tip'),
            SMB3EncryptEnabledTip: this.get('service.share_term_createCIFS_SMB3Encrypt_confirm_msg'),
            enableUnencryptedAccessTip: this.get('service.share_term_changeCIFS_Unencrypted_Access_confirm_msg'),
            fileNameExtensionFilter: this.get('service.share_task_filenameextensionfiltering_description'),
            //离线文件缓存模式
            offLineFileModeTip: this.get('service.share_task_createcifs_offlineCacheMode_tip'),
            modeNotNoneTip: this.get('service.share_term_changeCIFS_OFFLINEMODE_confirm_msg'),
            enableCaTip: this.get('service.share_term_changeSMB2_CA_ENABLED_confirm_msg'),
        },
        error: {
            // copy from 的共享不能为空
            copyFromRequired: this.get('service.share_term_createMode_copyfrom_valid'),
            // 至少选择一项
            atLeastSelectOneTip: this.get('common.selectOneObject_lab'),
            adUserValidation: this.get('service.share_term_createCIFS_addADUser_name_validation1'),
            adGroupValidation: this.get('service.share_term_createCIFS_addADUserGroup_name_validation'),
            otherDomainUsrValidation: this.get('service.share_term_createCIFS_addUser_validation'),
            // 已加入权限列表
            alreadySelected: this.get('service.share_term_createCIFS_addUser_exist_tip'),
            // 不能包含*:\\?”<>|中的任意一个特殊字符，且每个特殊字符串(%d或%w)只能出现一次。
            relative_path: this.get('service.share_term_createCIFSHomedir_relativeDirectory_valid'),
            // 用户名不能包含字符\"[]<>+:;,?=|@和空格，“\\”和“.”不能作为首尾字符，“*”只能位于末尾，“\\*”只能出现一次。
            userName: this.get('service.share_term_createCIFSHomedir_userName_err_valid'),
            smartRecommendDataTimeoutMsg: this.get('rest.getSmartRecommendDataTimeout_msg'),
        },
        highRisk: {
            deleteCIFS: this.get('service.share_deletecifs_task_deletecifs_confirmation_message'),
            removeClient: this.get('service.share_deletenfs_task_removeShare_confirmation_message'),
            modifyCIFS: this.get('service.share_modifycifs_task_modifyShare_confirmation_message'),
            modifyClient: this.get('service.share_modifycifs_task_modifyClient_confirmation_message'),
            setHomedirRule: this.get('service.share_term_createCIFSHomedir_setMapRule_confirm_msg'),
        },
        rest: {
            // 创建CIFS共享{0}
            createCIFS: this.get('rest.CreateCIFSShare_msg'),
            // 修改CIFS共享{0}
            modifyShare: this.get('rest.ModifyCIFSShare_msg'),
            // 删除CIFS共享{0}
            deleteCIFS: this.get('rest.DeleteCIFSShare_msg'),
            // 增加CIFS共享用户组）（{0}）{1}
            addClients: this.get('rest.AddCIFSUserorGroup_msg'),
            // 修改CIFS共享用户组）（{0}）{1}
            modifyClient: this.get('rest.modifyCIFSUserorGroup_msg'),
            // 删除CIFS共享用户组）（{0}）{1}
            removeClients: this.get('rest.deleteCIFSUserorGroup_msg'),
            /**
             * 创建增加客户端任务
             */
            addTaskAddClient: this.get('rest.createTask_addUserorUserGroup_msg'),
            homedir: {
                // 创建homedir共享{0}
                create: this.get('rest.createCIFSHomedirShare_msg'),
                // 修改homedir共享{0}
                modify: this.get('rest.modifyCIFSHomedirShare_msg'),
                // 删除homedir共享{0}
                delete: this.get('rest.deleteCIFSHomedirShare_msg'),
                // 增加homedir共享用户组）（{0}）{1}
                addClients: this.get('rest.addCIFSHomedirUserorGroup_msg'),
                // 修改homedir共享用户组）（{0}）{1}
                modifyClient: this.get('rest.modifyCIFSHomedirUserorGroup_msg'),
                // 删除homedir共享用户组）（{0}）{1}
                removeClients: this.get('rest.deleteCIFSHomedirUserorGroup_msg'),
                // 增加CIFS Homedir映射规则（用户：{0}，共享路径：{1}）
                addRule: this.get('rest.addCIFSHomedirMapRule_msg'),
                // 修改CIFS Homedir映射规则（用户：{0}，共享路径：{1}）
                modifyRule: this.get('rest.modifyCIFSHomedirMapRule_msg'),
                // 移除CIFS Homedir映射规则（用户：{0}，共享路径：{1}）
                removeRule: this.get('rest.deleteCIFSHomedirMapRule_msg'),
            },
        },
    };

    /**
     * 是否是homedir
     *
     * 否 0
     *
     * 是 1
     *
     * 全部 2 ，全部在表格的beforequery中构造，不在这里
     */
    homedirOpts: SelectOption[] = [
        {
            label: this.i18n.value.no,
            value: ShareTypeE.Normal,
        },
        {
            label: this.i18n.value.yes,
            value: ShareTypeE.Homedir,
        },
    ];

    /**
     * 用户、用户组类型
     */
    userTypeOpts: SelectOption[] = [
        {
            label: this.i18n.label.everyone,
            value: UserTypeE.Everyone,
        },
        ...ProductConfig.isHideWindowsUserFeature ? [] : [
            {
                label: this.i18n.label.windowsLocalUser,
                value: UserTypeE.User,
            },
            {
                label: this.i18n.label.windowsLocalUserGroup,
                value: UserTypeE.UserGroup,
            },
        ],
        ...ProductConfig.isHideAdDomain ? [] : [
            {
                label: this.i18n.label.adDomainUser,
                value: UserTypeE.AD,
            },
            {
                label: this.i18n.label.adDomainGroup,
                value: UserTypeE.ADGroup,
            },
        ],
        {
            label: this.i18n.label.unixLocalUser,
            value: UserTypeE.UnixUser,
        },
        {
            label: this.i18n.label.unixLocalUserGroup,
            value: UserTypeE.UnixUserGroup,
        },
        {
            label: this.i18n.label.ldapDomainUser,
            value: UserTypeE.Ldap,
        },
        {
            label: this.i18n.label.ldapDomainGroup,
            value: UserTypeE.LdapGroup,
        },
        {
            label: this.i18n.label.nisDomainUser,
            value: UserTypeE.Nis,
        },
        {
            label: this.i18n.label.nisDomainGroup,
            value: UserTypeE.NisGroup,
        },
    ];

    /**
     * 权限级别，
     *
     * 只读 0
     *
     * 完全控制 1
     *
     * 禁止 2
     *
     * 读写（不允许删除重命名） 5
     */
    permissionLevelOpts: SelectOption[] = [
        {
            label: this.i18n.label.readonly,
            value: 0,
        },
        {
            label: this.i18n.label.entrieControl,
            value: 1,
        },
        {
            label: this.i18n.label.forbidden_listbox,
            value: 2,
        },
        {
            label: this.i18n.label.rwNotAllowDelRename,
            value: 5,
        },
    ];

    /**
     * 权限级别（审计日志文件系统创建CIFS用户/用户组只支持只读和禁止权限）
     *
     * 只读 0
     *
     * 禁止 2
     *
     */
    auditLogFsPermissionLevelOpts: SelectOption[] = [
        {
            label: this.i18n.label.readonly,
            value: 0,
        },
        {
            label: this.i18n.label.forbidden_listbox,
            value: 2,
        },
    ];

    /**
     * 写入模式，
     *
     * 同步 0
     *
     * 异步 1
     */
    syncOpts: SelectOption[] = [
        {
            label: this.i18nService.get('service.share_term_addClient_writeMode_sync_lab'),
            value: '0',
        },
        {
            label: this.i18nService.get('service.share_term_addClient_writeMode_async_lab'),
            value: '1',
        },
    ];

    /**
     * 组类型
     * 1 内置组,
     * 2 自定义组
     */
    groupTypeOpt = [
        {
            value: '1',
            label: this.get('service.accessUser_userGroupType_default_value'),
        },
        {
            value: '2',
            label: this.get('service.accessUser_userGroupType_userCreate_value'),
        },
    ];

    /**
     * 显示Snapshot
     *
     */
    enableShowSnapOpts: SelectOption[] = [
        {
            label: this.i18nService.get('common.enabled_switch_column'),
            value: 'true',
        },
        {
            label: this.i18nService.get('common.disabled_switch_column'),
            value: 'false',
        },
    ];

    /**
     * 离线文件缓存模式
     */
    fileModeOpts = [
        {
            label: this.i18nService.get('common.EnumDefine_OFFLINEFILE_MODE_E_None'),
            value: OffLineFileModeE.None,
        },
        {
            label: this.i18nService.get('common.EnumDefine_OFFLINEFILE_MODE_E_Manual'),
            value: OffLineFileModeE.Manual,
        },
        {
            label: this.i18nService.get('common.EnumDefine_OFFLINEFILE_MODE_E_Documents'),
            value: OffLineFileModeE.Documents,
        },
        {
            label: this.i18nService.get('common.EnumDefine_OFFLINEFILE_MODE_E_Programs'),
            value: OffLineFileModeE.Programs,
        },
    ];

    /** 本地用户/用户组 */
    localUsrAndGrp = [UserTypeE.User, UserTypeE.UserGroup, UserTypeE.UnixUser, UserTypeE.UnixUserGroup];
    /** 域用户 */
    domainUsr = [UserTypeE.AD, UserTypeE.Ldap, UserTypeE.Nis];
    /** 域用户组 */
    domainGrp = [UserTypeE.ADGroup, UserTypeE.LdapGroup, UserTypeE.NisGroup];
    /** 不需要开“高级”就可以选 */
    baseType = [UserTypeE.Everyone, UserTypeE.User, UserTypeE.UserGroup, UserTypeE.AD, UserTypeE.ADGroup];

    configGuideMap = {
        [UserTypeE.AD]: {
            textConfigs: [{
                label: this.i18nService.get('service.quota_term_create_addUser_configAD_info'),
            }],
            guideItems: [GuideItemE.AD],
        },
        [UserTypeE.ADGroup]: {
            textConfigs: [{
                label: this.i18nService.get('service.quota_term_create_addUser_configAD_info'),
            }],
            guideItems: [GuideItemE.AD],
        },
        [UserTypeE.Ldap]: {
            textConfigs: [{
                label: this.i18nService.get('service.quota_term_create_addUser_configLDAP_info'),
            }],
            guideItems: [GuideItemE.LDAP],
        },
        [UserTypeE.LdapGroup]: {
            textConfigs: [{
                label: this.i18nService.get('service.quota_term_create_addUser_configLDAP_info'),
            }],
            guideItems: [GuideItemE.LDAP],
        },
        [UserTypeE.Nis]: {
            textConfigs: [{
                label: this.i18nService.get('service.quota_term_create_addUser_configNIS_info'),
            }],
            guideItems: [GuideItemE.NIS],
        },
        [UserTypeE.NisGroup]: {
            textConfigs: [{
                label: this.i18nService.get('service.quota_term_create_addUser_configNIS_info'),
            }],
            guideItems: [GuideItemE.NIS],
        },
    };

    userLabelMap = {
        [DomainTypeE.Local]: this.i18n.label.windowsLocalUser,
        [DomainTypeE.Unix]: this.i18n.label.unixLocalUser,
        [DomainTypeE.AD]: this.i18n.label.adDomainUser,
        [DomainTypeE.Ldap]: this.i18n.label.ldapDomainUser,
        [DomainTypeE.Nis]: this.i18n.label.nisDomainUser,
    };

    groupLabelMap = {
        [DomainTypeE.Local]: this.i18n.label.windowsLocalUserGroup,
        [DomainTypeE.Unix]: this.i18n.label.unixLocalUserGroup,
        [DomainTypeE.AD]: this.i18n.label.adDomainGroup,
        [DomainTypeE.Ldap]: this.i18n.label.ldapDomainGroup,
        [DomainTypeE.Nis]: this.i18n.label.nisDomainGroup,
    };

    /**
     * SmartGUI返回的类型映射表
     */
    guiUserTypeMap = {
        [DomainTypeE.Local]: {
            [AuthNameE.Everyone]: UserTypeE.Everyone,
            [AuthNameE.Single]: UserTypeE.User,
            [AuthNameE.Group]: UserTypeE.UserGroup,
        },
        [DomainTypeE.AD]: {
            [AuthNameE.Single]: UserTypeE.AD,
            [AuthNameE.Group]: UserTypeE.ADGroup,
        },
        [DomainTypeE.Ldap]: {
            [AuthNameE.Single]: UserTypeE.Ldap,
            [AuthNameE.Group]: UserTypeE.LdapGroup,
        },
        [DomainTypeE.Nis]: {
            [AuthNameE.Single]: UserTypeE.Nis,
            [AuthNameE.Group]: UserTypeE.NisGroup,
        },
        [DomainTypeE.Unix]: {
            [AuthNameE.Single]: UserTypeE.UnixUser,
            [AuthNameE.Group]: UserTypeE.UnixUserGroup,
        },
    };

    constructor(
        private i18nService: I18nService,
    ) {
    }

    /**
     * 是否启用显示
     */
    renderEnable = (enabled: boolean | string): string => enabled === true || enabled === 'true' ?
        this.get('common.enabled_switch_column') :
        this.get('common.disabled_switch_column');

    /**
     * 获取用户/用户组类型，NAME以'@'开头的是用户组
     * @param data CIFS共享用户/用户组
     */
    getUserOrGroupType = (data: CIFSClient): string => {
        if (Number(data.DOMAINTYPE) === DomainTypeE.Local) {
            if (data.NAME.startsWith('@')) {
                return this.get('service.share_term_createCIFS_addUser_userType_winLocalUserGroup_listbox');
            }
            return this.get('service.share_term_createCIFS_addUser_userType_winLocalUser_listbox');
        }
        if (Number(data.DOMAINTYPE) === DomainTypeE.AD) {
            if (data.NAME.startsWith('@')) {
                return this.i18n.label.adDomainGroup;
            }
            return this.i18n.label.adDomainUser;
        }

        return '';
    };

    /**
     * 获取用户/用户组类型，NAME以'@'开头的是用户组
     * @param data CIFS共享用户/用户组
     */
    getUpperCaseUserOrGroupType = (data: CIFSClient): string => {
        // 用户组
        if (data.NAME.startsWith('@')) {
            return this.groupLabelMap[Number(data.DOMAINTYPE)];
        }

        // 用户
        return this.userLabelMap[Number(data.DOMAINTYPE)];
    };

    /**
     * 获取用户类型
     */
    getUserType = (data: SmartguiCifsshareReceived): UserTypeE => {
        const domainType = Number(data.domaintype);
        const authName: AuthNameE = Number(data.authname);
        return this.guiUserTypeMap[domainType][authName];
    };

    get(key, params?, colon?): string {
        return this.i18nService.get(key, params, colon);
    }

    requiredErr(name: string): string {
        return this.get('common.null_validation', [name]);
    }

    rangeByteLengthErr(min: number, max: number): string {
        return this.get('common.length_msg', [min, max]);
    }

    rangeByteOneLengthErr(min: number, max: number): string {
        return this.get('common.length_oneValue_tip', [min, max]);
    }

    commonNameErr(name: string): string {
        return this.get('common.name_vadlitaion', [name]);
    }

    deleteResult(name: string): string {
        return this.get('rest.delete_result_tip', [name]);
    }

    repeatErr(name: string): string {
        return this.get('common.existence_validation', [name]);
    }

    otherDomainValidation(isUsr: boolean, min: number, max: number): string {
        return isUsr ?
            this.get('service.share_term_createCIFS_addUser_tip', [min, max]) :
            this.get('service.share_term_createCIFS_addUserGroup_tip', [min, max]);
    }
}
